import React from 'react';

import routeEnpoints from 'route/endpoints';

export const MEDALLION_TABS = {
    WORKFLOWS: 'workflows',
    THEMES: 'themes',
};

export const MEDALLION_TAB_ITEMS = [
    {
        id: MEDALLION_TABS.WORKFLOWS,
        label: 'Workflows',
        path: routeEnpoints.workflows,
    },
    {
        id: MEDALLION_TABS.THEMES,
        label: 'Themes',
        path: routeEnpoints.themes,
        isNew: true,
    },
];

export const getThemeEditorPath = (nanoid) => `${routeEnpoints.themes}/${nanoid}`;

export const getWorkflowDetailPath = (nanoid) => `${routeEnpoints.workflows}/${nanoid}`;

export const WORKFLOWS_BANNER_MESSAGE =
    'Configure workflows for your users to provide a seamless verification experience.';

export const THEME_NAME_MAX_LENGTH = 50;

export const BUTTON_VARIANTS = {
    CONTAINED: 'contained',
    OUTLINED: 'outlined',
};

// No longer offered, but stored themes may still carry it, so it is read as
// OUTLINED rather than left to render as an unknown value.
export const RETIRED_BUTTON_VARIANT = 'text';

export const THEME_PROPERTY_KEYS = {
    PRIMARY_COLOR: 'primaryColor',
    SECONDARY_COLOR: 'secondaryColor',
    FONT_FAMILY: 'fontFamily',
    BUTTON: 'button',
    TEXT_INPUT_VARIANT: 'textInputVariant',
    MODAL_BACKGROUND_COLOR: 'modalBackgroundColor',
    MODAL_BORDER_RADIUS: 'modalBorderRadius',
    OVERLAY_COLOR: 'overlayColor',
    HEADER_COLOR: 'headerColor',
    NAVBAR_COLOR: 'navbarColor',
    STEPPER_BG_COLOR: 'stepperBgColor',
};

// Ported from components/ThemeEditor/menuConfig.js (fieldDefaultValues).
export const THEME_DEFAULTS = {
    [THEME_PROPERTY_KEYS.PRIMARY_COLOR]: '#154683',
    [THEME_PROPERTY_KEYS.SECONDARY_COLOR]: '#cde3fc',
    [THEME_PROPERTY_KEYS.FONT_FAMILY]: 'Lora',
    [THEME_PROPERTY_KEYS.BUTTON]: {
        variant: BUTTON_VARIANTS.CONTAINED,
        borderRadius: '6px',
    },
    [THEME_PROPERTY_KEYS.TEXT_INPUT_VARIANT]: 'outlined',
    [THEME_PROPERTY_KEYS.MODAL_BACKGROUND_COLOR]: '#FFFFFF',
    [THEME_PROPERTY_KEYS.MODAL_BORDER_RADIUS]: '0px',
    [THEME_PROPERTY_KEYS.OVERLAY_COLOR]: '#CDE4FF',
    [THEME_PROPERTY_KEYS.HEADER_COLOR]: '#EFF5FD',
    [THEME_PROPERTY_KEYS.NAVBAR_COLOR]: '#EFF5FD',
    [THEME_PROPERTY_KEYS.STEPPER_BG_COLOR]: '#ffffff',
};

export const THEME_CONFIG_KEYS = Object.values(THEME_PROPERTY_KEYS);

// Themes surface copy

export const THEMES_BANNER_MESSAGE =
    'Manage the brand themes used across your Medallion workflows.';

export const THEMES_LOAD_ERROR_MESSAGE =
    "We couldn't load your themes. Refresh the page to try again.";

// Split around the link, which is markup rather than copy.
export const THEMES_EMPTY_HINT = {
    BEFORE: 'This is the only theme you have. Edit it to match your brand, or ',
    ACTION: 'duplicate it',
    AFTER: ' to build a second one alongside it.',
};

export const CANNOT_DELETE_DEFAULT_TOOLTIP =
    'This is your default theme. Make another theme the default first, then delete this one.';

export const CANNOT_DELETE_ONLY_THEME_TOOLTIP =
    'This is your only theme. Create another one before deleting it.';

export const getThemeInUseTooltip = (workflowsCount) =>
    `Used by ${workflowsCount} ${
        workflowsCount === 1 ? 'workflow' : 'workflows'
    }. Move those workflows to another theme first.`;

export const THEME_UPDATED_AT_FORMAT = 'MMM dd, yyyy';

export const THEMES_SKELETON_CARD_COUNT = 6;

// Turned on by hand for the migration release. Delete this and MigrationBanner
// together once it is old news.
export const SHOW_MIGRATION_BANNER = true;

// Returned when a theme name is already taken, which the form shows on the
// name field rather than as a toast.
export const HTTP_CONFLICT = 409;

export const getThemeUsageLabel = (count) => {
    if (!count) {
        return 'Not used by any workflow';
    }

    return `Used by ${count} ${count === 1 ? 'workflow' : 'workflows'}`;
};

const pluralizeWorkflow = (count) => (count === 1 ? 'workflow' : 'workflows');

export const getThemeUsageTitle = (count) => `Used in ${count} ${pluralizeWorkflow(count)}`;

export const getThemeUsageImpact = (count) =>
    count === 1
        ? 'Changes to this theme apply to this workflow.'
        : 'Changes to this theme apply to all of them.';

export const getThemeUsageDeleteHint = (count) =>
    `To delete this theme, move ${
        count === 1 ? 'this workflow' : 'these workflows'
    } to another theme first.`;

export const THEME_USAGE_DEFAULT_HINT =
    "New workflows start with this theme because it's your default.";

export const CREATE_THEME_BUTTON_LABEL = 'Create Theme';

export const THEME_FORM_MODES = {
    CREATE: 'create',
    DUPLICATE: 'duplicate',
};

export const THEME_FORM_COPY = {
    NAME_LABEL: 'Theme name',
    NAME_PLACEHOLDER: 'Partner Verification',
    CANCEL: 'Cancel',
};

export const THEME_FORM_MODE_COPY = {
    [THEME_FORM_MODES.CREATE]: {
        TITLE: 'Create theme',
        SUBMIT: 'Create theme',
        getDescription: () =>
            'Name your theme and create it for use in your workflows. You can update the branding anytime.',
    },
    [THEME_FORM_MODES.DUPLICATE]: {
        TITLE: 'Duplicate theme',
        SUBMIT: 'Duplicate',
        getDescription: (sourceName) =>
            `Create a new theme based on ${sourceName} to customize without altering the original.`,
    },
};

export const getDuplicateThemeName = (name) => `${name} (Copy)`;

export const getThemeNameTakenError = (name) =>
    `A theme called “${name}” already exists. Choose another name.`;

export const THEME_CREATE_FAILED_MESSAGE = "We couldn't create the theme. Please try again.";

export const THEME_RENAME_FAILED_MESSAGE = "We couldn't rename the theme. Please try again.";

export const SET_DEFAULT_THEME_LABEL = 'Set as default';

export const getThemeActionsLabel = (name) => `Actions for ${name}`;

export const getSetDefaultSuccessMessage = (name) => `${name} is now your default theme.`;

export const SET_DEFAULT_THEME_FAILED_MESSAGE =
    "We couldn't change your default theme. Please try again.";

export const DELETE_THEME_COPY = {
    CANCEL: 'Cancel',
    SUBMIT: 'Delete theme',
    TITLE: 'Delete Theme?',
    getDescription: (name) => (
        <>
            Are you sure you want to delete the theme <b>{name}</b>?
        </>
    ),
    FAILED: "We couldn't delete the theme. Please try again.",
    // The list showed it unused, so a workflow picked it up after the page loaded.
    getInUseError: (name, count) =>
        `Couldn't delete ${name}. It's now used by ${count} ${pluralizeWorkflow(count)}. Move ${
            count === 1 ? 'it' : 'them'
        } to another theme first.`,
};

export const DISCARD_CHANGES_COPY = {
    TITLE: 'Discard your changes?',
    getDescription: (name) => (
        <>
            Your changes to <b>{name}</b> have not been saved. If you leave now they will be lost.
        </>
    ),
    CANCEL: 'Keep editing',
    CONFIRM: 'Discard changes',
};

export const UNSAVED_CHANGES_LABEL = 'Unsaved changes';

export const SAVE_THEME_CONFIRM_COPY = {
    TITLE: 'Update this theme?',
    NO_USAGE: "This theme isn't linked to any workflow yet.",
    CANCEL: 'Cancel',
    CONFIRM: 'Save changes',
    FAILED: "We couldn't save your changes. Please try again.",
    LOGO_FAILED: "We couldn't read your logo file. Upload a different image and save again.",
};

export const getSaveThemeStatLabel = (count) => (
    <>
        This theme is currently used by <b>{count}</b> {count === 1 ? 'workflow' : 'workflows'}.
        Saving updates them immediately.
    </>
);

export const SAVE_THEME_UPDATE_NOTE =
    'Everyone using these workflows will see the new look as soon as you save.';

export const THEME_SAVED_TOAST_MESSAGE = 'Theme saved';

export const THEME_SAVE_FAILED_TOAST_MESSAGE = "Couldn't save theme";

export const MIGRATION_BANNER_COPY = {
    TITLE: 'Your workflow branding is now a set of themes',
    BODY: 'Each workflow kept exactly the branding it had — we just gave it a name so it can be reused. Nothing changed for your customers. Rename these, or point several workflows at one theme, whenever you like.',
    DISMISS: 'Got it',
};

// Theme editor

export const THEME_EDITOR_SECTION_IDS = {
    LOGO: 'logo',
    COLOR: 'color',
    FONT: 'font',
    BUTTONS: 'buttons',
    ADVANCED: 'advanced',
};

export const THEME_EDITOR_SECTIONS = [
    { id: THEME_EDITOR_SECTION_IDS.LOGO, label: 'Logo' },
    { id: THEME_EDITOR_SECTION_IDS.COLOR, label: 'Color' },
    { id: THEME_EDITOR_SECTION_IDS.FONT, label: 'Font' },
    { id: THEME_EDITOR_SECTION_IDS.BUTTONS, label: 'Buttons' },
    { id: THEME_EDITOR_SECTION_IDS.ADVANCED, label: 'Advanced' },
];

// Which config keys the "changed" dot on each section rail item watches.
// Logo isn't here — it lives outside config, on draft.logo directly.
export const THEME_SECTION_CONFIG_FIELDS = {
    [THEME_EDITOR_SECTION_IDS.COLOR]: ['primaryColor', 'secondaryColor'],
    [THEME_EDITOR_SECTION_IDS.FONT]: ['fontFamily'],
    [THEME_EDITOR_SECTION_IDS.BUTTONS]: ['button'],
    [THEME_EDITOR_SECTION_IDS.ADVANCED]: [
        'textInputVariant',
        'modalBackgroundColor',
        'modalBorderRadius',
        'overlayColor',
        'headerColor',
        'navbarColor',
        'stepperBgColor',
    ],
};

export const THEME_LOAD_ERROR_MESSAGE =
    "We couldn't load this theme. Refresh the page to try again.";

export const WORKFLOW_THEME_FIELD_LABEL = 'Theme';

export const WORKFLOW_THEME_DEFAULT_TAG = 'Default';

export const WORKFLOW_THEME_PREVIEW_COPY = {
    EXPAND: 'Expand theme preview',
    getTitle: (themeName) => `${themeName} preview`,
    SUBTITLE: 'Preview of the verification screens your users will see with this theme.',
};

export const WORKFLOW_THEME_HELPER_TEXT = {
    DEFAULT: 'Your default theme is applied. Create and edit themes on the Themes tab.',
    SELECTED: 'You can edit this theme on the Themes tab.',
};

export const SWITCH_WORKFLOW_THEME_COPY = {
    TITLE: 'Change theme?',
    DESCRIPTION: {
        BEFORE: 'This will apply ',
        AFTER: ' across all verification screens. Save the workflow to make changes live.',
    },
    CANCEL: 'Cancel',
    CONFIRM: 'Change theme',
};

export const PREVIEW_WORKFLOWS_LABEL = 'Preview affected workflows';

export const PREVIEW_SAMPLE_LABEL = 'Previewing a sample workflow';

export const PREVIEW_STATUS_COPY = {
    LOADING: 'Updating preview…',
    ERROR: "Couldn't load this workflow. Showing the sample workflow instead.",
};

export const PREVIEW_CANVAS_LABEL = 'Screen previews';

export const EDITOR_SMALL_SCREEN_COPY = {
    TITLE: 'Open the theme editor on a larger screen',
    BODY: 'The live preview needs more width than this screen has. You can still create, rename and delete themes from the Themes list.',
    ACTION: 'Back to Themes',
};

// Alphabetical, the order a search list should return them in — the design's
// mock shows 'Rubik' out of order only because it is the selected one there.
export const THEME_FONT_OPTIONS = [
    'Comfortaa',
    'IBM Plex Sans',
    'Inter',
    'Lora',
    'Montserrat',
    'Noto Sans JP',
    'Poppins',
    'Roboto',
    'Rubik',
];

export const FONT_APPLIES_TO_CAPTION = 'Applies to every verification screen.';

export const FONT_SEARCH_PLACEHOLDER = 'Search fonts';

export const getNoFontsFoundMessage = (query) => `No fonts match “${query}”.`;

export const BUTTON_STYLE_OPTIONS = [
    { label: 'Filled', variant: BUTTON_VARIANTS.CONTAINED },
    { label: 'Outlined', variant: BUTTON_VARIANTS.OUTLINED },
];

// Named rather than pixel numbers, per the ticket. The legacy editor's '15px'
// is retired: it renders identically to Pill, since the radius clamps at half
// the button height.
export const BUTTON_RADIUS_OPTIONS = [
    { label: 'Square', borderRadius: '0px' },
    { label: 'Soft', borderRadius: '6px' },
    { label: 'Pill', borderRadius: '44px' },
];

export const BUTTONS_APPLIES_TO_CAPTION = 'Applies to every button in the verification flow.';

// Ported from menuConfig.js's MODAL_BORDER_RADIUS options.
export const MODAL_RADIUS_OPTIONS = ['0px', '10px', '20px', '30px', '40px', '50px'];

export const INPUT_VARIANT_OPTIONS = [
    { label: 'Outlined', value: 'outlined' },
    { label: 'Filled', value: 'filled' },
];

export const ADVANCED_APPLIES_TO_CAPTION = 'Fine-tunes surfaces across the verification flow.';

export const LOGO_ACCEPTED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

export const LOGO_MAX_SIZE_BYTES = 2 * 1024 * 1024;

export const LOGO_RECOMMENDED_HEIGHT_PX = 90;

export const LOGO_REQUIREMENT_IDS = {
    FORMAT: 'format',
    SIZE: 'size',
};

// Height is guidance only, never validated.
export const LOGO_REQUIREMENTS = [
    { id: LOGO_REQUIREMENT_IDS.FORMAT, label: 'PNG, JPEG or WEBP' },
    { id: LOGO_REQUIREMENT_IDS.SIZE, label: 'Maximum file size 2 MB' },
    { id: 'height', label: `Recommended logo height ${LOGO_RECOMMENDED_HEIGHT_PX} px` },
];

export const LOGO_EMPTY_CAPTION = 'No logo added yet';

export const SECTION_LABEL_WIDTHS = ['55%', '70%', '50%', '65%', '45%'];

export const PREVIEW_SLOT_COUNT = 4;
